<template>
    <div style="position: relative">
        <div ref="map" id="ol-map-container" class="w-100" :style="{height: mapHeight}"></div>
        <div v-if="tooltip" v-html="tooltipHtml"
             class="map-tooltip" :style="{left: tooltipX + 'px', top: tooltipY + 'px'}">
        </div>
        <edit-plot-dialog ref="editPlotDlg"/>
    </div>
</template>

<script>
import OpenLayersMap from "ol/Map";
import {defaults as defaultInteractions} from "ol/interaction";
import {defaults as defaultControls} from "ol/control";
import View from "ol/View";
import {fromLonLat} from "@/olMap/coordUtils";
import ScaleLineControl from "@/olMap/control/ScaleLine";
import MapTypeControl from "@/olMap/control/MapType";
import RoadTypeControl from "@/olMap/control/RoadType";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {getLayers} from "@/olMap/mapLayers";
import * as $ from "jquery";
import * as olExtent from "ol/extent";
import * as MsgBox from "@/utils/msgBox";
import {
    createFeature, createPlotStyle, getPlotLabelItem, initPlotTypes, placeLabelItems, plotDisplayName, plotTypes
} from "@/olMap/featureUtils";
import * as Html from "@/utils/html";
import BrokerWechat from "@/services/brokerWechat";
import User from "@/services/user";
import Plot from '@/services/plot';
import EditPlotDialog from "@/components/EditPlotDialog";

export default {
    name: "WechatLandmark",
    components: {EditPlotDialog},
    data() {
        return {
            mapHeight: "100vh",
            tooltip: "",
            tooltipX: 0,
            tooltipY: 0,
        };
    },
    methods: {
        getClientSize() {
            return [
                window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
                window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight,
            ];
        },
        async load(name, nearSimilar) {
            let landmark = await BrokerWechat.loadLandmark(name, nearSimilar || "");
            if (landmark) {
                landmark.coordinate = fromLonLat([landmark.lon, landmark.lat]);
                landmark.aliases = landmark.aliases || [];
                landmark.displayName = plotDisplayName(landmark);
                let coordinates = [landmark.coordinate];
                if (Array.isArray(landmark.nearSimilar) && landmark.nearSimilar.length > 0) {
                    for (let item of landmark.nearSimilar) {
                        item.coordinate = fromLonLat([item.lon, item.lat]);
                        item.aliases = item.aliases || [];
                        item.displayName = plotDisplayName(item);
                        coordinates.push(item.coordinate);
                    }
                }
                this.setViewport(coordinates);
                this.landmark = landmark;
                this.updateLandmarkLayer();
            } else {
                MsgBox.alert(this, `未找到地标：“${name}”`);
            }
        },
        setViewport(coordinates) {
            let size = this.getClientSize();
            let px = Math.max(50, Math.round(size[0] * 0.1));
            let py = Math.max(50, Math.round(size[1] * 0.1));
            this.map.getView().fit(olExtent.boundingExtent(coordinates), {
                padding: [py, px, py, px], maxZoom: 11,
            });
        },
        createLandmarkFeature(landmark, direction, near) {
            let tooltipLines = [`名称：${landmark.name}`, `类型：${landmark.type}`];
            if (landmark.aliases.length > 0) {
                tooltipLines.push(`别名：${landmark.aliases.join()}`);
            }
            if (landmark.river) {
                tooltipLines.push(`河流：${landmark.river}`);
            }
            let hasCursor = (landmark.cursor || landmark.cursor === 0);
            if (hasCursor) {
                tooltipLines.push(`里程：${landmark.cursor}`);
            }
            if (landmark.note) {
                tooltipLines.push(`备注：${landmark.note}`);
            }
            let style;
            if (near) {
                let type = Object.assign({}, plotTypes.find(x => x.type === landmark.type));
                type.style = "stroke:black;fill:blue;";
                style = createPlotStyle(landmark, direction, null, null, type);
            } else {
                style = createPlotStyle(landmark, direction);
            }
            return createFeature(landmark.coordinate, style, {landmark, tooltip: tooltipLines.join("\n")});
        },
        updateLandmarkLayer() {
            let features = [];
            if (this.landmark) {
                let occupiedRects = [];
                let labelItems = [];
                let plots = [this.landmark]
                    .concat(Array.isArray(this.landmark.nearSimilar) ? this.landmark.nearSimilar : []);
                plots.forEach(p => {
                    let pixel = this.map.getPixelFromCoordinate(p.coordinate);
                    if (pixel) {
                        occupiedRects.push([pixel[0] - 7, pixel[1] - 22, pixel[0] + 7, pixel[1]]);
                    }
                    let labelItem = getPlotLabelItem(p);
                    if (labelItem) {
                        labelItems.push(labelItem);
                    }
                });
                labelItems = placeLabelItems(labelItems, this.map, occupiedRects);
                plots.forEach(p => {
                    let labelItem = labelItems.find(l => l.plot && l.id === p.id);
                    let direction = labelItem && labelItem.direction;
                    features.push(this.createLandmarkFeature(p, direction, p.id !== this.landmark.id));
                });
            }
            this.landmarkLayer.setSource(new VectorSource({features}));
        },
        onPointerMove(e) {
            this.lastPointerPixel = this.map.getEventPixel(e.originalEvent);
            if (e.dragging) {
                this.updateTooltip();
            } else {
                this.updateCursor();
            }
        },
        onClick(e) {
            if (e && e.originalEvent && e.originalEvent.button === 0) {
                let pixel = this.map.getEventPixel(e.originalEvent);
                let feature = this.getTooltipFeature(pixel);
                if (feature) {
                    let landmark = feature.get("landmark");
                    let typeInfo = plotTypes.find(x => landmark && landmark.id && x.type === landmark.type);
                    if (typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable)) {
                        this.modifyLandmark(landmark);
                    }
                }
            }
        },
        updateCursor() {
            let cursor = "default";
            let feature = this.updateTooltip();
            if (feature) {
                let landmark = feature.get("landmark");
                let typeInfo = plotTypes.find(x => landmark && landmark.id && x.type === landmark.type);
                if (typeInfo && (this.isAdmin ? typeInfo.adminEditable : typeInfo.userEditable)) {
                    cursor = "pointer";
                }
            }
            document.body.style.cursor = cursor;
        },
        updateTooltip() {
            if (this.lastPointerPixel) {
                let feature = this.getTooltipFeature(this.lastPointerPixel);
                if (feature) {
                    this.tooltip = feature.get('tooltip');
                    this.tooltipX = this.lastPointerPixel[0] + 7;
                    this.tooltipY = this.lastPointerPixel[1] + 20;
                } else if (this.tooltip) {
                    this.tooltip = "";
                }
                return feature;
            }
        },
        getTooltipFeature(pixel) {
            let feature = null;
            this.map.forEachFeatureAtPixel(pixel, function (x) {
                feature = feature || (x.get('tooltip') ? x : null);
            }, {hitTolerance: 5});
            return feature;
        },
        async modifyLandmark(landmark) {
            document.body.style.cursor = "default";
            let plot = Object.assign({}, landmark);
            let result = await this.$refs.editPlotDlg.modal(plot);
            if (result !== void (0)) {
                plot = await Plot.load(landmark.id);
                Object.assign(landmark, plot);
                this.updateLandmarkLayer();
            }
        }
    },
    computed: {
        isAdmin() {
            return User.user && User.user.admin;
        },
        tooltipHtml() {
            return Html.format(this.tooltip);
        },
    },
    async mounted() {
        $(document.body).css("overflow", "hidden");

        this.landmarkLayer = new VectorLayer({source: new VectorSource({features: []})});
        let center = fromLonLat([119.511392, 32.21177]);
        let zoom = 11;
        this.map = new OpenLayersMap({
            target: 'ol-map-container',
            interactions: defaultInteractions({
                altShiftDragRotate: false,
                pinchRotate: false,
                doubleClickZoom: false,
            }),
            controls: defaultControls().extend([
                new ScaleLineControl({units: 'metric'}),
                new MapTypeControl(),
                new RoadTypeControl(),
            ]),
            loadTilesWhileInteracting: true,
            view: new View({center, zoom, maxZoom: 19}),
            layers: getLayers().concat([this.landmarkLayer]),
        });
        this.map.on("pointermove", e => this.onPointerMove(e));
        this.map.on('singleclick', e => this.onClick(e));
        this.map.on("moveend", () => this.updateLandmarkLayer());

        await initPlotTypes();
        let name = this.$route.query["name"] || "";
        let showNear = this.$route.query["showNear"] || "";
        if (name) {
            await this.load(name, showNear);
        }
    },
    created() {
        this.sizeHandler = () => this.mapHeight = this.getClientSize()[1] + "px";
        window.onresize = this.sizeHandler;
        this.sizeHandler();
    },
    destroyed() {
        $(document.body).css("overflow", "");
    },
}
</script>

<style scoped>
.map-tooltip {
    position: absolute;
    top: 0;
    left: 0;
    padding: .25em .5em;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    z-index: 100;
}
</style>

<style>
.ol-map-type {
    bottom: 10px;
    left: 10px;
}

.ol-road-type {
    bottom: 10px;
    left: 160px;
}

.ol-control.ol-map-type, .ol-control.ol-road-type {
    display: flex;
}

.ol-control.ol-map-type button, .ol-control.ol-road-type button {
    width: auto;
    height: auto;
    line-height: normal;
    font-size: 18px;
    font-weight: normal;
    padding: 5px 10px;
}

.ol-control.ol-map-type button.selected, .ol-control.ol-road-type button.selected {
    text-decoration: none;
    background-color: rgba(0, 60, 136, .8);
}

.ol-scale-line {
    top: auto;
    left: auto;
    right: 10px;
    bottom: 10px;
}

.ol-zoom {
    left: auto;
    top: auto;
    right: 10px;
    bottom: 40px;
}

.ol-zoom button {
    font-size: 1.5rem;
}

@media (max-width: 512px) {
    .ol-road-type {
        bottom: 55px;
        left: 10px;
    }
}

.ol-tooltip {
    position: relative;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    color: white;
    padding: 4px 8px;
    opacity: 0.7;
    white-space: nowrap;
    font-size: 12px;
}

.ol-tooltip-measure {
    opacity: 1;
    font-weight: bold;
}

.ol-tooltip-static {
    background-color: orangered;
    color: white;
    border: 1px solid white;
}

.ol-tooltip-measure:before,
.ol-tooltip-static:before {
    border-top: 6px solid rgba(0, 0, 0, 0.5);
    border-right: 6px solid transparent;
    border-left: 6px solid transparent;
    content: "";
    position: absolute;
    bottom: -6px;
    margin-left: -7px;
    left: 50%;
}

.ol-tooltip-static:before {
    border-top-color: orangered;
}

.ol-tooltip-static-close-btn {
    font-size: 1.4rem;
    line-height: 0.8rem;
    height: 1rem;
    cursor: pointer;
}

.ol-tooltip-static-close-btn:hover {
}

.ol-tooltip-static-close-btn:active {
}

.ol-event-transparent {
    pointer-events: none;
}

.ol-event-solid {
    pointer-events: auto;
}

.ol-circle {
    background: transparent;
    width: 10px;
    height: 10px;
    border: 1px solid black;
    border-radius: 5px;
}
</style>
